import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';

export default function StudentVerificationPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching users:', err);
        setLoading(false);
      }
    };
    fetchAllUsers();
  }, []);

  const handleApprove = async (uid) => {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      isStudent: true,
      studentDiscountExpiresAt: Timestamp.fromDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
      studentVerificationPending: false,
      studentVerificationUploadURL: null,
    });
    setUsers(prev => prev.map(user => user.id === uid ? { ...user, isStudent: true, studentVerificationPending: false } : user));
  };

  const handleReject = async (uid) => {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      studentVerificationPending: false,
      studentVerificationUploadURL: null,
    });
    setUsers(prev => prev.map(user => user.id === uid ? { ...user, studentVerificationPending: false } : user));
  };

  const handleCancelDiscount = async (uid) => {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      isStudent: false,
      studentDiscountExpiresAt: null,
    });
    setUsers(prev => prev.map(user => user.id === uid ? { ...user, isStudent: false } : user));
  };

  const filteredUsers = users.filter(u =>
    (u.studentVerificationPending || u.isStudent) &&
    (
      u.email?.toLowerCase().includes(filter.toLowerCase()) ||
      u.name?.toLowerCase().includes(filter.toLowerCase())
    )
  );

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>🎓 Student Verification & Discount Admin</h2>

      <input
        type="text"
        placeholder="Search by name or email"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        style={styles.search}
      />

      {loading ? (
        <p>Loading...</p>
      ) : filteredUsers.length === 0 ? (
        <p>No matching student records found.</p>
      ) : (
        <div style={styles.grid}>
          {filteredUsers.map(user => (
            <div key={user.id} style={styles.card}>
              <div style={styles.meta}>
                <strong>Name:</strong> {user.name || 'N/A'}<br />
                <strong>Email:</strong> {user.email || 'N/A'}<br />
                <strong>Submitted:</strong> {user.studentVerificationSubmittedAt?.toDate().toLocaleString() || 'Unknown'}<br />
                <strong>Status:</strong>{' '}
                {user.studentVerificationPending ? '⏳ Pending Review' :
                 user.isStudent ? '✅ Discount Active' : '❌ Not Student'}
              </div>

              {user.studentVerificationUploadURL && (
                <a href={user.studentVerificationUploadURL} target="_blank" rel="noopener noreferrer">
                  <img
                    src={user.studentVerificationUploadURL}
                    alt="Student ID"
                    style={styles.image}
                  />
                </a>
              )}

              <div style={styles.buttonRow}>
                {user.studentVerificationPending && (
                  <>
                    <button onClick={() => handleApprove(user.id)} style={styles.approve}>Approve</button>
                    <button onClick={() => handleReject(user.id)} style={styles.reject}>Reject</button>
                  </>
                )}
                {user.isStudent && !user.studentVerificationPending && (
                  <button onClick={() => handleCancelDiscount(user.id)} style={styles.cancel}>
                    Cancel Discount
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  search: {
    marginBottom: '1.5rem',
    padding: '0.5rem',
    fontSize: '0.95rem',
    width: '100%',
    maxWidth: '360px',
    border: '1px solid #ccc',
    borderRadius: '6px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '1rem',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  meta: {
    fontSize: '0.875rem',
    color: '#333',
    marginBottom: '0.5rem',
    lineHeight: '1.4',
  },
  image: {
    width: '100%',
    height: 'auto',
    marginBottom: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #ddd',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  approve: {
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  reject: {
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  cancel: {
    backgroundColor: '#d97706',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
};
